/**
 * The account-password policy, client side.
 *
 * Twin of api/src/password_policy.py, which is AUTHORITATIVE (the server
 * enforces it on every path that sets an account password). This copy exists
 * only to tell the user what is wrong before the round trip. The two are held
 * to the same vector table (passwordPolicyVectors.json) by
 * api/tests/test_password_policy.py and __tests__/passwordPolicy.spec.js.
 *
 * The rule (it replaces the old 10-character minimum, ADR 0050):
 *   at least 8 characters, AND at least one of: a symbol, an upper-case
 *   letter, or a digit -- measured after stripping surrounding whitespace.
 *
 * What counts (Unicode general categories, same as the backend):
 *   - upper-case letter: Lu (title-case Lt does not count)
 *   - digit: Nd (any script's decimal digit)
 *   - symbol: any P* (punctuation, underscore included) or S* (symbol, emoji
 *     included). Spaces, controls and zero-width characters are NOT symbols.
 *   - length is counted in code points (an emoji is one character).
 *
 * NOT for sign-in forms (login is never blocked by policy) and NOT for the
 * store SHARED password (it has its own rule).
 */

export const MIN_PASSWORD_LENGTH = 8

export const PASSWORD_POLICY_MESSAGE =
  `Password must be at least ${MIN_PASSWORD_LENGTH} characters and include ` +
  'a number, an upper-case letter, or a symbol.'

export const MAX_PASSWORD_LENGTH = 256

export const PASSWORD_TOO_LONG_MESSAGE = `Password must be at most ${MAX_PASSWORD_LENGTH} characters.`

export const PASSWORD_REQUIRED_MESSAGE = 'A new password is required.'

// Python str.strip() whitespace, NOT String.prototype.trim(): trim() strips
// U+FEFF and keeps U+001C-U+001F / U+0085, strip() does the opposite. The
// server is authoritative, so the twin must measure what the server will.
const PY_WHITESPACE =
  '\\t\\n\\v\\f\\r\\u001c-\\u001f \\u0085\\u00a0\\u1680\\u2000-\\u200a' +
  '\\u2028\\u2029\\u202f\\u205f\\u3000'
const STRIP_RE = new RegExp(`^[${PY_WHITESPACE}]+|[${PY_WHITESPACE}]+$`, 'g')

const REQUIRED_CLASS = /[\p{Lu}\p{Nd}\p{P}\p{S}]/u

/**
 * @param {unknown} value what the user typed
 * @returns {true | string} true when acceptable, else a message safe to show
 *   (it never contains the password)
 */
export function validatePassword(value) {
  if (typeof value !== 'string') return PASSWORD_REQUIRED_MESSAGE
  const normalized = value.replace(STRIP_RE, '')
  if (!normalized) return PASSWORD_REQUIRED_MESSAGE
  // Oversize first (O(1) on UTF-16 length: no code point holds more than two
  // units), before counting code points or scanning classes.
  if (normalized.length > MAX_PASSWORD_LENGTH * 2) return PASSWORD_TOO_LONG_MESSAGE
  const length = [...normalized].length
  if (length > MAX_PASSWORD_LENGTH) return PASSWORD_TOO_LONG_MESSAGE
  if (
    length < MIN_PASSWORD_LENGTH ||
    !REQUIRED_CLASS.test(normalized)
  ) {
    return PASSWORD_POLICY_MESSAGE
  }
  return true
}

/** For `:rules` on a Vuetify text field that SETS an account password. */
export const passwordRules = [validatePassword]
